// OWNER: Agent C. Adjust/close/delete loans — the backend already supports
// PATCH/DELETE /loans/:id, just no UI hooked it up. Lives inside Admin,
// which is already RequireAdmin-gated at the route level.
import { useState } from "react";
import { Text } from "@cloudflare/kumo/components/text";
import { Badge } from "@cloudflare/kumo/components/badge";
import { Button } from "@cloudflare/kumo/components/button";
import { Table } from "@cloudflare/kumo/components/table";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Dialog } from "@cloudflare/kumo/components/dialog";
import { useKumoToastManager } from "@cloudflare/kumo/components/toast";
import { PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { formatMoney } from "@shared/format";
import { useLoans, deleteLoan } from "../../lib/hooks";
import { TableSkeleton } from "../../components/Skeletons";
import { LoanFormDialog, type LoanFormState } from "../loans/LoanFormDialog";

export function LoansManager() {
  const loans = useLoans();
  const toasts = useKumoToastManager();
  const [formState, setFormState] = useState<LoanFormState>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  function refreshed(message: string) {
    toasts.add({ title: message, variant: "success" });
    loans.reload();
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteLoan(deleteTarget.id);
      refreshed(`Deleted ${deleteTarget.name}`);
      setDeleteTarget(null);
    } catch (e) {
      toasts.add({ title: "Couldn't delete", content: e instanceof Error ? e.message : undefined, variant: "error" });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Text variant="heading" as="h2">
          Loans
        </Text>
      </div>

      {loans.status === "loading" && <TableSkeleton rows={3} />}
      {loans.status === "error" && <Text variant="error">{loans.error.message}</Text>}
      {loans.status === "success" && loans.data.length === 0 && <Empty size="sm" title="No loans yet" />}
      {loans.status === "success" && loans.data.length > 0 && (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>Name</Table.Head>
              <Table.Head>Lender</Table.Head>
              <Table.Head>Outstanding</Table.Head>
              <Table.Head>Status</Table.Head>
              <Table.Head />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loans.data.map((loan) => (
              <Table.Row key={loan.id}>
                <Table.Cell>{loan.name}</Table.Cell>
                <Table.Cell>{loan.lender ?? "—"}</Table.Cell>
                <Table.Cell>{formatMoney(loan.outstandingPrincipal, loan.currency)}</Table.Cell>
                <Table.Cell>
                  <Badge variant={loan.status === "active" ? "success" : "neutral"}>{loan.status}</Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      shape="square"
                      variant="ghost"
                      icon={PencilSimpleIcon}
                      aria-label={`Adjust ${loan.name}`}
                      onClick={() => setFormState({ mode: "edit", loan })}
                    />
                    <Button
                      size="sm"
                      shape="square"
                      variant="secondary-destructive"
                      icon={TrashIcon}
                      aria-label={`Delete ${loan.name}`}
                      onClick={() => setDeleteTarget({ id: loan.id, name: loan.name })}
                    />
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <LoanFormDialog
        // Remounts the whole dialog (and its useState initializers) per distinct
        // target — see LoanFormDialog's own comment for why this can't live one
        // level down on just <Dialog.Root>.
        key={`${formState != null}-${formState?.mode === "edit" ? formState.loan.id : "new"}`}
        state={formState}
        onClose={() => setFormState(null)}
        onSaved={() => refreshed("Loan updated")}
      />

      <Dialog.Root open={deleteTarget != null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <Dialog className="p-6">
          <Dialog.Title className="text-lg font-semibold">Delete {deleteTarget?.name}?</Dialog.Title>
          <Text variant="secondary" size="sm">
            This also deletes every payment recorded against this loan. This can&apos;t be undone.
          </Text>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" loading={deleting} onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
    </div>
  );
}
